import { useState } from 'react'
import { Ring, sparklinePoints } from './charts'
import type { HealthCategory } from '../../../data/ServerApiClient'
import { deltaColor, formatDelta, healthToneColors } from './cfoHealthLabels'
import { RatioScoreCard } from './RatioScoreCard'
import { CfoCategoryWeightEditor } from './CfoCategoryWeightEditor'

// One collapsed category row (mini score ring + grade + weight + "what moved it" + mini-trend). Expands
// to reveal the scored per-ratio cards underneath — progressive disclosure so the default view stays the
// overall score + five categories, never a wall of ratios.

export function CfoHealthCategoryRow({
  category,
  showGrades = true,
  onSelectRatio,
  onSaveRatioWeights,
  busy,
  expanded,
  onToggle,
}: {
  category: HealthCategory
  showGrades?: boolean
  onSelectRatio?: (ratioId: string) => void
  /** Persist this category's per-metric weights (metric_id → relative weight; 0 excludes). */
  onSaveRatioWeights?: (categoryKey: string, ratioWeights: Record<string, number>) => Promise<void> | void
  busy?: boolean
  expanded: boolean
  onToggle: () => void
}) {
  const [showAll, setShowAll] = useState(false)
  const score = category.score ?? null
  const tone = score != null ? healthToneColors(score) : null
  const weightPct = Math.round(category.weight * 100)
  const seriesValues = category.series.map((p) => p.score)
  const canExpand = category.ratios.length > 0
  const notScored = score == null

  // Concentrate on the strong-signal metrics the AI actually weighted; the rest are context, collapsed
  // behind "+N more" so the expanded category isn't a wall of ratios. If the AI weighted none (provisional
  // equal weighting), every ratio is "key" and there's nothing to collapse.
  const keyRatios = category.ratios.filter((r) => r.weight > 0)
  const secondaryRatios = category.ratios.filter((r) => r.weight <= 0)
  const hasKey = keyRatios.length > 0
  const shownRatios = !hasKey || showAll ? category.ratios : keyRatios

  const sparkColor =
    seriesValues.length >= 2 && seriesValues[seriesValues.length - 1] < seriesValues[0]
      ? 'var(--neg, #c0492f)'
      : 'var(--pos, #0e7c86)'

  return (
    <div style={{ border: '1px solid var(--hair)', borderRadius: 'var(--r1, 10px)', background: 'var(--surface)' }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        disabled={!canExpand}
        style={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '44px 1fr auto',
          gap: 12,
          alignItems: 'center',
          padding: '12px 14px',
          background: 'transparent',
          border: 'none',
          cursor: canExpand ? 'pointer' : 'default',
          textAlign: 'left',
          font: 'inherit',
        }}
      >
        {notScored ? (
          <span style={{ width: 44 }} />
        ) : (
          <Ring
            value={score / 100}
            label={Math.round(score)}
            size={44}
            stroke={5}
            color={tone!.color}
            ariaLabel={`${category.label} health ${Math.round(score)} of 100`}
          />
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {canExpand ? (
              <span aria-hidden="true" style={{ fontSize: 13, color: 'var(--muted)' }}>
                {expanded ? '▾' : '▸'}
              </span>
            ) : null}
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{category.label}</span>
            {notScored ? (
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>Not scored</span>
            ) : showGrades ? (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: tone!.color,
                  background: tone!.bg,
                  padding: '1px 7px',
                  borderRadius: 999,
                }}
              >
                {category.grade}
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
            {weightPct}% of the score
            {category.top_mover ? ` · ${category.top_mover}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {category.delta_1mo != null && Math.abs(category.delta_1mo) >= 0.5 ? (
            <span style={{ fontSize: 12.5, fontWeight: 600, color: deltaColor(category.delta_1mo) }}>
              {formatDelta(category.delta_1mo)}
            </span>
          ) : null}
          {seriesValues.length >= 2 ? (
            <svg width={64} height={20} viewBox="0 0 64 20" preserveAspectRatio="none" role="img" aria-label={`${category.label} health trend`}>
              <polyline
                points={sparklinePoints(seriesValues, 64, 20)}
                fill="none"
                stroke={sparkColor}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : null}
        </div>
      </button>
      {expanded && canExpand ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 8,
            padding: '2px 14px 14px',
          }}
        >
          {shownRatios.map((ratio) => (
            <RatioScoreCard key={ratio.id} ratio={ratio} showGrades={showGrades} onSelect={onSelectRatio} />
          ))}
        </div>
      ) : null}
      {expanded && canExpand && hasKey && secondaryRatios.length > 0 ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          style={{
            display: 'block',
            width: '100%',
            padding: '0 14px 12px',
            marginTop: -4,
            background: 'transparent',
            border: 'none',
            textAlign: 'left',
            font: 'inherit',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--brand, #1b4965)',
            cursor: 'pointer',
          }}
        >
          {showAll
            ? '− Show fewer'
            : `+ ${secondaryRatios.length} more metric${secondaryRatios.length === 1 ? '' : 's'} (not weighted into the score)`}
        </button>
      ) : null}
      {expanded && canExpand && onSaveRatioWeights ? (
        <div style={{ padding: '0 14px 14px' }}>
          <CfoCategoryWeightEditor
            category={category}
            busy={busy}
            onSave={(rw) => onSaveRatioWeights(category.key, rw)}
          />
        </div>
      ) : null}
    </div>
  )
}

export default CfoHealthCategoryRow
